import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { columnsApi, tasksApi, usersApi, type TaskFilters, type TaskPatch } from '../lib/api.ts'
import type { Task } from '../lib/types.ts'

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: TaskFilters) => ['tasks', filters] as const,
  one: (id: number) => ['task', id] as const,
}

export function useColumns() {
  return useQuery({
    queryKey: ['columns'],
    queryFn: async () => (await columnsApi.list()).columns,
    staleTime: 60_000,
  })
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => (await usersApi.list()).users,
    staleTime: 5 * 60_000,
  })
}

export function useTasks(filters: TaskFilters = {}) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: async () => (await tasksApi.list(filters)).tasks,
  })
}

/**
 * Every mutation ends with an invalidate rather than a hand-written cache patch (RULES §3). Stage 4
 * replaces the local invalidate with one driven by the /ws change event.
 */
function useInvalidateTasks(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: taskKeys.all })
    void queryClient.invalidateQueries({ queryKey: ['task'] })
  }
}

export function useCreateTask() {
  const invalidate = useInvalidateTasks()
  return useMutation({
    mutationFn: (input: { title: string; columnId?: number }) => tasksApi.create(input),
    onSuccess: invalidate,
  })
}

/**
 * Inline edits of status and priority are optimistic (ui-spec §5.3) with a rollback, the way
 * useMoveTask already was. Without it a click on the status pill sat unchanged until the round
 * trip finished and the list re-rendered — the two edits the spec calls out as needing to feel
 * instant were the two that did not.
 *
 * The snapshot covers every cached task list, not one key: the same task shows up under several
 * filter combinations and restoring only the visible one would leave the others wrong.
 */
export function useUpdateTask() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateTasks()

  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: TaskPatch }) => tasksApi.patch(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.all })
      const previous = queryClient.getQueriesData<Task[]>({ queryKey: taskKeys.all })

      for (const [key, tasks] of previous) {
        if (!tasks) continue
        queryClient.setQueryData<Task[]>(
          key,
          tasks.map((task) =>
            task.id === id
              ? {
                  ...task,
                  ...(patch.columnId !== undefined ? { columnId: patch.columnId } : {}),
                  ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
                }
              : task,
          ),
        )
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      // Put every list back exactly as it was; the banner that explains it lives in the view.
      for (const [key, tasks] of ctx?.previous ?? []) queryClient.setQueryData(key, tasks)
    },
    onSettled: invalidate,
  })
}

export function useArchiveTask() {
  const invalidate = useInvalidateTasks()
  return useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      archived ? tasksApi.archive(id) : tasksApi.unarchive(id),
    onSuccess: invalidate,
  })
}

export interface MoveVariables {
  id: number
  columnId: number
  beforeTaskId?: number
  afterTaskId?: number
}

/**
 * Drag & drop is one of the two places latency actually hurts, so it updates optimistically
 * (RULES §3). On failure the snapshot goes back — a card that stays where it was dropped while the
 * server refused the move is worse than no optimism at all.
 */
export function useMoveTask(filters: TaskFilters = {}, optimistic = true) {
  const queryClient = useQueryClient()
  const key = taskKeys.list(filters)

  return useMutation({
    mutationFn: ({ id, ...rest }: MoveVariables) => tasksApi.move(id, rest),
    onMutate: async (vars: MoveVariables) => {
      // While the socket is down the UI must not pretend the move landed (RULES §3).
      if (!optimistic) return { previous: undefined }
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Task[]>(key)
      if (previous) queryClient.setQueryData<Task[]>(key, reorder(previous, vars))
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.all })
    },
  })
}

/** Mirrors the server's placement so the optimistic view matches what comes back. */
function reorder(tasks: Task[], vars: MoveVariables): Task[] {
  const moving = tasks.find((t) => t.id === vars.id)
  if (!moving) return tasks

  const rest = tasks.filter((t) => t.id !== vars.id)
  const target = rest.filter((t) => t.columnId === vars.columnId)
  const others = rest.filter((t) => t.columnId !== vars.columnId)

  const anchorId = vars.beforeTaskId ?? vars.afterTaskId
  let index = target.length
  if (anchorId !== undefined) {
    const found = target.findIndex((t) => t.id === anchorId)
    if (found !== -1) index = vars.beforeTaskId !== undefined ? found : found + 1
  }

  target.splice(index, 0, { ...moving, columnId: vars.columnId })

  // Positions are the server's business; ours only need to sort correctly until the refetch lands.
  return [...others, ...target.map((t, i) => ({ ...t, position: i + 1 }))]
}
